import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';

const DocumentationStep = ({ formData, setFormData, errors }) => {
  const [uploadProgress, setUploadProgress] = useState({});
  const [dragOver, setDragOver] = useState({});

  const documentTypes = [
    {
      key: 'portfolio',
      label: 'Portfolio/Press Kit *',
      description: 'PDF, images ou liens vers votre travail',
      required: true,
      accept: '.pdf,.jpg,.jpeg,.png,.zip'
    },
    {
      key: 'businessLicense',
      label: 'Licence commerciale',
      description: 'KBIS, certificat d\'inscription, etc.',
      required: false,
      accept: '.pdf,.jpg,.jpeg,.png'
    },
    {
      key: 'insurance',
      label: 'Assurance professionnelle',
      description: 'Certificat d\'assurance responsabilité civile',
      required: false,
      accept: '.pdf,.jpg,.jpeg,.png'
    },
    {
      key: 'references',
      label: 'Références clients',
      description: 'Témoignages, contrats précédents',
      required: false,
      accept: '.pdf,.doc,.docx'
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = (documentType, files) => {
    const fileList = Array.from(files);
    
    // Simulate upload progress
    setUploadProgress(prev => ({
      ...prev,
      [documentType]: 0
    }));

    const interval = setInterval(() => {
      setUploadProgress(prev => {
        const currentProgress = prev?.[documentType] || 0;
        if (currentProgress >= 100) {
          clearInterval(interval);
          return prev;
        }
        return {
          ...prev,
          [documentType]: currentProgress + 10
        };
      });
    }, 200);

    // Store files in form data
    setFormData(prev => ({
      ...prev,
      documents: {
        ...prev?.documents,
        [documentType]: fileList
      }
    }));
  };

  const handleDragOver = (e, documentType) => {
    e?.preventDefault();
    setDragOver(prev => ({ ...prev, [documentType]: true }));
  };

  const handleDragLeave = (e, documentType) => {
    e?.preventDefault();
    setDragOver(prev => ({ ...prev, [documentType]: false }));
  };

  const handleDrop = (e, documentType) => {
    e?.preventDefault();
    setDragOver(prev => ({ ...prev, [documentType]: false }));
    const files = e?.dataTransfer?.files;
    handleFileUpload(documentType, files);
  };

  const removeFile = (documentType, fileIndex) => {
    setFormData(prev => ({
      ...prev,
      documents: {
        ...prev?.documents,
        [documentType]: prev?.documents?.[documentType]?.filter((_, index) => index !== fileIndex) || []
      }
    }));
  };

  return (
    <div className="space-y-6">
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Documents et Vérifications
        </h3>
        
        <div className="space-y-6">
          {documentTypes?.map((docType) => (
            <div key={docType?.key} className="border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="font-medium text-foreground">{docType?.label}</h4>
                  <p className="text-sm text-muted-foreground">{docType?.description}</p>
                </div>
                {docType?.required && (
                  <span className="text-xs bg-error/10 text-error px-2 py-1 rounded">
                    Requis
                  </span>
                )}
              </div>
              
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  dragOver?.[docType?.key]
                    ? 'border-accent bg-accent/5' :'border-border hover:border-accent/50'
                }`}
                onDragOver={(e) => handleDragOver(e, docType?.key)}
                onDragLeave={(e) => handleDragLeave(e, docType?.key)}
                onDrop={(e) => handleDrop(e, docType?.key)}
              >
                <Icon name="Upload" size={32} className="mx-auto mb-3 text-muted-foreground" />
                <p className="text-sm text-foreground mb-2">
                  Glissez-déposez vos fichiers ici ou
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => document.getElementById(`file-${docType?.key}`)?.click()}
                >
                  Parcourir les fichiers
                </Button>
                <input
                  id={`file-${docType?.key}`}
                  type="file"
                  multiple
                  accept={docType?.accept}
                  className="hidden"
                  onChange={(e) => handleFileUpload(docType?.key, e?.target?.files)}
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Formats acceptés: {docType?.accept?.replace(/\./g, '')?.toUpperCase()}
                </p>
              </div>
              
              {/* Upload Progress */}
              {uploadProgress?.[docType?.key] !== undefined && uploadProgress?.[docType?.key] < 100 && (
                <div className="mt-3">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-muted-foreground">Téléchargement...</span>
                    <span className="text-muted-foreground">{uploadProgress?.[docType?.key]}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-accent h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress?.[docType?.key]}%` }}
                    />
                  </div>
                </div>
              )}
              
              {/* Uploaded Files */}
              {formData?.documents?.[docType?.key]?.length > 0 && (
                <div className="mt-3 space-y-2">
                  {formData?.documents?.[docType?.key]?.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-muted rounded-lg p-3">
                      <div className="flex items-center space-x-3">
                        <Icon name="File" size={16} className="text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium text-foreground">{file?.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {(file?.size / 1024 / 1024)?.toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFile(docType?.key, index)}
                        className="text-error hover:text-error"
                      >
                        <Icon name="Trash2" size={16} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {errors?.[docType?.key] && (
                <p className="text-sm text-error mt-2">{errors?.[docType?.key]}</p>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Informations Bancaires
        </h3>
        
        <div className="space-y-4">
          <Input
            label="Nom du titulaire du compte *"
            type="text"
            placeholder="Nom complet ou raison sociale"
            value={formData?.accountHolderName || ''}
            onChange={(e) => handleInputChange('accountHolderName', e?.target?.value)}
            error={errors?.accountHolderName}
            required
            description="Pour les virements de commissions"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="IBAN *"
              type="text"
              placeholder="FR76 1234 5678 9012 3456 7890 123"
              value={formData?.iban || ''}
              onChange={(e) => handleInputChange('iban', e?.target?.value)}
              error={errors?.iban}
              required
            />
            
            <Input
              label="BIC/SWIFT"
              type="text"
              placeholder="BNPAFRPPXXX"
              value={formData?.bic || ''}
              onChange={(e) => handleInputChange('bic', e?.target?.value)}
              error={errors?.bic}
              description="Code international de votre banque"
            />
          </div>
          
          <Input
            label="Nom de la banque"
            type="text"
            placeholder="BNP Paribas"
            value={formData?.bankName || ''}
            onChange={(e) => handleInputChange('bankName', e?.target?.value)}
            error={errors?.bankName}
          />
        </div>
      </div>
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Vérifications Finales
        </h3>
        
        <div className="space-y-4">
          <Checkbox
            label="Je certifie que toutes les informations fournies sont exactes *"
            checked={formData?.certifyInformation || false}
            onChange={(e) => handleInputChange('certifyInformation', e?.target?.checked)}
            error={errors?.certifyInformation}
            required
            description="Déclaration sur l'honneur de l'exactitude des données"
          />
          
          <Checkbox
            label="J'autorise Artis.io à vérifier mes informations auprès de tiers *"
            checked={formData?.authorizeVerification || false}
            onChange={(e) => handleInputChange('authorizeVerification', e?.target?.checked)}
            error={errors?.authorizeVerification}
            required
            description="Nécessaire pour la validation de votre compte"
          />
          
          <Checkbox
            label="Je souhaite recevoir des notifications par SMS"
            checked={formData?.smsNotifications || false}
            onChange={(e) => handleInputChange('smsNotifications', e?.target?.checked)}
            description="Alertes importantes et mises à jour de commissions"
          />
        </div>
      </div>
      <div className="bg-accent/5 border border-accent/20 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={20} className="text-accent mt-0.5" />
          <div>
            <h4 className="font-medium text-foreground mb-1">Processus de validation</h4>
            <p className="text-sm text-muted-foreground">
              Votre inscription sera examinée dans les 24-48 heures. Vous recevrez un e-mail de confirmation 
              une fois votre compte validé. En cas de questions, notre équipe vous contactera directement.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentationStep;